/**
 * ADM-02 学生与报名复合列表-档案区（student-registry-admin-ui:SC-01~SC-04，design 前端页面变更）。
 *
 * 学生维度列：学号（链接开详情浮层）、姓名、性别、年级/学段、归属校区、监护人手机号（脱敏）、
 * 学生状态（一期存储状态 2 值）、更新时间。筛选：关键词/学段/状态 + 分页。
 * 校区口径（O-23 收编锁定模式）：页内无校区筛选，唯一读顶栏全局校区范围
 * （`admin-global-campus-switch` 第 12 接入页；「全部校区」不携带参数，指定校区按既有线格式 `campus` 携带）。
 * 工具栏：新增/导入/导出/代家长报名（导出筛选与列表同口径）。列宽固定不支持拖拽。报名维度列预留空位。
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, Input, Select, Space, Table, Tag } from 'antd';
import { message } from '../../utils/antd-message';
import {
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { EllipsisLink } from "../../components/EllipsisCell";
import {
  studentsApi,
  type GradeStage,
  type StudentGender,
  type StudentListItem,
  type StudentStatus,
} from '../../api/students.api';
import { genderLabel, gradeStageLabel, toSelectOptions } from '../../api/labels';
import { MaskedMobile } from './MaskedMobile';
import { StudentCreateModal } from './StudentCreateModal';
import { StudentImportModal } from './StudentImportModal';
import { StudentDetailDrawer } from './StudentDetailDrawer';
import { StudentEditModal } from './StudentEditModal';
import type { StudentDetail } from '../../api/students.api';
import { useGlobalCampusScope } from '../../auth/GlobalCampusContext';
import { EnrollmentSection, type EnrollmentSectionHandle } from './EnrollmentSection';
import { EnrollmentCreateModal } from './EnrollmentCreateModal';
import { CONVERSION_STATUS_LABEL, type EnrollmentItem } from '../../api/enrollments.api';
import { useListExport } from '../../hooks/useListExport';
import { useHasPermission } from '../../auth/HasPermission';

/** 学生状态标签（一期展示存储状态 2 值）。 */
function StudentStatusTag({ status }: { status: StudentStatus }) {
  const cfg =
    status === "STUDYING"
      ? { color: "green", label: "在读" }
      : { color: "default", label: "已归档" };
  return <Tag color={cfg.color}>{cfg.label}</Tag>;
}

/** 学生报名摘要（由报名维度区块聚合上报，填充「报名状态」列）。 */
export interface StudentEnrollmentSummary {
  count: number;
  latest: string;
}

export interface StudentListPageProps {
  enrollmentSummary?: Map<string, StudentEnrollmentSummary>;
  /** 工具栏代建提交成功后通知（复合页用于同步刷新下方课程报名区块）。 */
  onEnrollmentSubmitted?: () => void;
}

export function StudentListPage({
  enrollmentSummary,
  onEnrollmentSubmitted,
}: StudentListPageProps = {}) {
  const [data, setData] = useState<StudentListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState<StudentStatus | undefined>();
  const [gradeStage, setGradeStage] = useState<GradeStage | undefined>();
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const [editStudent, setEditStudent] = useState<StudentDetail | null>(null);
  // 校区口径唯一读全局（O-23 锁定模式收编）：无页内校区筛选与临时态
  const { scope } = useGlobalCampusScope();
  const canExport = useHasPermission('ADM-02', 'EXPORT');
  const canCreate = useHasPermission('ADM-02', 'CREATE');
  const { exporting: exportingList, runExport } = useListExport('students');

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const result = await studentsApi.listStudents({
        keyword: keyword || undefined,
        status,
        gradeStage,
        campus: scope === 'ALL' ? undefined : scope,
        page,
        pageSize,
      });
      setData(result.items);
      setTotal(result.total);
    } catch (err) {
      message.error(err instanceof Error ? err.message : "加载学生列表失败");
    } finally {
      setLoading(false);
    }
  }, [keyword, status, gradeStage, scope, page, pageSize]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const columns: ColumnsType<StudentListItem> = [
    {
      title: "学号",
      dataIndex: "studentNo",
      key: "studentNo",
      width: 140,
      ellipsis: true,
      fixed: "left",
      render: (no: string, record) => (
        <EllipsisLink
          value={no}
          testId={`student-no-${record.id}`}
          onClick={() => setDrawerId(record.id)}
        />
      ),
    },
    { title: "姓名", dataIndex: "name", key: "name", width: 100 },
    {
      title: "性别",
      dataIndex: "gender",
      key: "gender",
      width: 60,
      render: (g: StudentGender) => genderLabel[g] ?? g,
    },
    {
      title: "年级/学段",
      dataIndex: "gradeStage",
      key: "gradeStage",
      width: 110,
      render: (g: GradeStage) => gradeStageLabel[g] ?? g,
    },
    {
      title: "归属校区",
      dataIndex: "homeCampusName",
      key: "homeCampusName",
      width: 120,
      render: (v?: string) => v ?? "-",
    },
    {
      title: "监护人手机号",
      dataIndex: "guardianMobile",
      key: "guardianMobile",
      width: 150,
      render: (mobile: string, record) => (
        <MaskedMobile studentId={record.id} masked={mobile} />
      ),
    },
    {
      title: "学生状态",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (s: StudentStatus) => <StudentStatusTag status={s} />,
    },
    {
      title: "更新时间",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 160,
      render: (v: string) => new Date(v).toLocaleString(),
    },
    // 报名状态（student-registry-admin-ui:SC-04 预留列，3-2 填充）：该学生报名条数 + 最新转化状态。
    // 摘要来自下方报名维度区块已加载数据（一期演示量级下同屏可见；跨页学生以「-」保守显示）。
    {
      title: "报名状态",
      key: "enrollmentSummary",
      width: 150,
      render: (_, record) => {
        const summary = enrollmentSummary?.get(record.id);
        if (!summary || summary.count === 0) {
          return <span style={{ color: "#ccc" }}>-</span>;
        }
        return (
          <span>
            {summary.count} 条 · {summary.latest}
          </span>
        );
      },
    },
  ];

  return (
    <Card
      title="学生档案"
      extra={
        <Space>
          {canCreate && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateOpen(true)}
              data-testid="btn-create-student"
            >
              新增
            </Button>
          )}
          {canCreate && (
            <Button
              icon={<UploadOutlined />}
              onClick={() => setImportOpen(true)}
              data-testid="btn-import-student"
            >
              导入
            </Button>
          )}
          {canExport && (
            <Button
              icon={<DownloadOutlined />}
              loading={exportingList}
              data-testid="btn-export-student"
              onClick={() =>
                void runExport({
                  keyword: keyword || undefined,
                  status,
                  gradeStage,
                  campus: scope === 'ALL' ? undefined : scope,
                })
              }
            >
              导出
            </Button>
          )}
          {canCreate && (
            <Button
              onClick={() => setEnrollOpen(true)}
              data-testid="btn-enroll-student"
            >
              代家长报名
            </Button>
          )}
        </Space>
      }
    >
      <Space style={{ marginBottom: 16 }} wrap>
        <Input.Search
          placeholder="姓名/学号/手机号"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onSearch={() => {
            setPage(1);
            void fetchList();
          }}
          style={{ width: 220 }}
          data-testid="filter-keyword"
        />
        <Select
          placeholder="学生状态"
          allowClear
          value={status}
          onChange={(v) => setStatus(v)}
          style={{ width: 120 }}
          options={[
            { value: "STUDYING", label: "在读" },
            { value: "ARCHIVED", label: "已归档" },
          ]}
          data-testid="filter-status"
        />
        <Select
          placeholder="年级/学段"
          allowClear
          value={gradeStage}
          onChange={(v) => setGradeStage(v)}
          style={{ width: 140 }}
          options={toSelectOptions(gradeStageLabel)}
          data-testid="filter-grade-stage"
        />
      </Space>
      <Table
        scroll={{ x: "max-content" }}
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
      <StudentCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          void fetchList();
        }}
      />
      <StudentImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => {
          setImportOpen(false);
          void fetchList();
        }}
      />
      <EnrollmentCreateModal
        open={enrollOpen}
        onClose={() => setEnrollOpen(false)}
        onSubmitted={() => {
          setEnrollOpen(false);
          void fetchList();
          onEnrollmentSubmitted?.();
        }}
      />
      <StudentDetailDrawer
        studentId={drawerId}
        open={!!drawerId}
        onClose={() => setDrawerId(null)}
        onEdit={(s) => {
          setEditStudent(s);
          setDrawerId(null);
        }}
        onChanged={() => void fetchList()}
      />
      <StudentEditModal
        student={editStudent}
        open={!!editStudent}
        onClose={() => setEditStudent(null)}
        onSaved={() => {
          setEditStudent(null);
          void fetchList();
        }}
      />
    </Card>
  );
}

/** ADM-02 学生与报名复合列表·报名维度区块（3-2 追加）。 */
export function StudentEnrollmentCompositePage() {
  const [enrollmentSummary, setEnrollmentSummary] = useState<
    Map<string, StudentEnrollmentSummary>
  >(new Map());
  /** 课程报名区块句柄：工具栏代建提交后同步刷新组列表（否则新报名单要手动刷新页面才出现）。 */
  const enrollmentSectionRef = useRef<EnrollmentSectionHandle>(null);

  // 报名维度区块每次加载后聚合：studentId -> { 条数, 最新转化标签 }，填充学生表「报名状态」列
  const handleEnrollmentsLoaded = useCallback((items: EnrollmentItem[]) => {
    const map = new Map<string, StudentEnrollmentSummary>();
    for (const item of items) {
      const prev = map.get(item.studentId);
      map.set(item.studentId, {
        count: (prev?.count ?? 0) + 1,
        latest: CONVERSION_STATUS_LABEL[item.conversionStatus],
      });
    }
    setEnrollmentSummary(map);
  }, []);

  return (
    <>
      <StudentListPage
        enrollmentSummary={enrollmentSummary}
        onEnrollmentSubmitted={() => enrollmentSectionRef.current?.refresh()}
      />
      <EnrollmentSection
        ref={enrollmentSectionRef}
        onItemsLoaded={handleEnrollmentsLoaded}
      />
    </>
  );
}
